import React from 'react';
import { Loader2 } from 'lucide-react';

export type SpinnerTone = 'brand' | 'current' | 'muted';

export interface SpinnerProps {
  /** Diameter in px. Default 20. */
  size?: number;
  /**
   * Color of the spinner.
   * - `brand` (default): on-brand lime (`var(--accent)`), dark-mode aware.
   * - `current`: no inline color — the color comes from `currentColor`, i.e.
   *   inherited text color (use inside buttons so it stays visible on colored
   *   backgrounds) or an explicit text color `className` (use to preserve a
   *   semantic color, e.g. `tone="current" className="text-amber-600"`).
   * - `muted`: legacy neutral gray. Escape hatch; discouraged.
   */
  tone?: SpinnerTone;
  className?: string;
  style?: React.CSSProperties;
  label?: string;
  /**
   * When true, the spinner is hidden from assistive tech (rendered
   * `aria-hidden`, with no `status` role). Use this when the spinner sits
   * inside an element that already has `role="status"` / `aria-live`, to avoid
   * nested/duplicate status announcements.
   */
  decorative?: boolean;
}

const TONE_COLOR: Record<SpinnerTone, string | undefined> = {
  brand: 'var(--accent)',
  // `current` emits no inline color so `currentColor` is driven by inherited
  // text color or an explicit text-color className.
  current: undefined,
  muted: 'var(--text-muted)',
};

/**
 * The single, canonical loading spinner for the app. Renders in the brand lime
 * by default. Every loading spinner should be this component — do not hand-roll
 * `animate-spin` circles or render bare `Loader2` icons.
 */
export function Spinner({
  size = 20,
  tone = 'brand',
  className = '',
  style,
  label = 'Loading',
  decorative = false,
}: SpinnerProps) {
  const a11yProps = decorative
    ? { 'aria-hidden': true }
    : { role: 'status', 'aria-label': label };
  const color = TONE_COLOR[tone];
  return (
    <Loader2
      {...a11yProps}
      className={`animate-spin ${className}`}
      style={{ width: size, height: size, ...(color ? { color } : {}), ...style }}
    />
  );
}

export interface FullPageSpinnerProps extends SpinnerProps {
  /** Optional message rendered under the spinner. */
  message?: string;
}

/**
 * Centers a {@link Spinner} in a full-height flex container — for route-level /
 * full-page loading states. Replaces the copy-pasted
 * "center a big spinner in a flex box" boilerplate.
 */
export function FullPageSpinner({ size = 40, message, ...rest }: FullPageSpinnerProps) {
  return (
    <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-4">
      <Spinner size={size} {...rest} />
      {message ? <p className="text-ui-text-secondary text-sm">{message}</p> : null}
    </div>
  );
}

export default Spinner;
